import React, { useEffect, useRef, useState } from 'react';
import { Phone, Search, Wrench, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const HowItWorks = () => {
  const [visibleSteps, setVisibleSteps] = useState(new Set());
  const stepRefs = useRef([]);
  
  const handleCall = () => {
    window.location.href = 'tel:0676583038';
  };

  const scrollToContact = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepIndex = parseInt(entry.target.dataset.step);
            setVisibleSteps(prev => new Set([...prev, stepIndex]));
          }
        });
      },
      { threshold: 0.3 }
    );

    stepRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      step: 1,
      icon: Phone,
      title: "Vous nous appelez",
      description: "Décrivez votre problème par téléphone ou via notre formulaire. Notre expert évalue l'urgence et programme l'intervention.",
      detail: "Réponse immédiate • Conseils sécurité • Planification rapide",
      color: "bg-red-500",
      lightColor: "bg-red-50",
      borderColor: "border-red-200"
    },
    {
      step: 2,
      icon: Search,
      title: "Diagnostic gratuit",
      description: "Notre technicien se déplace gratuitement, identifie précisément le problème et vous propose la solution adaptée.",
      detail: "Devis gratuit • Sans engagement • Expertise professionnelle",
      color: "bg-blue-500", 
      lightColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      step: 3,
      icon: Wrench,
      title: "Intervention sécurisée",
      description: "Destruction du nid avec un équipement professionnel et des techniques adaptées à chaque type de nuisible.",
      detail: "Équipement pro • Techniques spécialisées • Sécurité maximale",
      color: "bg-yellow-500",
      lightColor: "bg-yellow-50", 
      borderColor: "border-yellow-200"
    },
    {
      step: 4,
      icon: CheckCircle,
      title: "Suivi & garantie",
      description: "Nettoyage complet, conseils préventifs et suivi pour éviter une nouvelle infestation. Garantie sur l'intervention.",
      detail: "Nettoyage inclus • Conseils préventifs • Garantie totale",
      color: "bg-green-500",
      lightColor: "bg-green-50",
      borderColor: "border-green-200"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-32 h-32 border-2 border-yellow-400 rounded-full animate-pulse-slow"></div>
        <div className="absolute bottom-32 right-32 w-24 h-24 border-2 border-black rounded-full animate-float"></div>
        <div className="absolute top-1/2 left-10 w-16 h-16 border-2 border-yellow-400 rounded-full"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 text-yellow-600 font-medium mb-4">
            <Clock className="h-5 w-5" />
            <span>Processus rapide et efficace</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Comment ça <span className="text-yellow-600">marche</span> ?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Un processus simple en 4 étapes pour résoudre rapidement et définitivement 
            votre problème de nuisibles.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-16">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isVisible = visibleSteps.has(index);
            const isEven = index % 2 === 0;
            
            return (
              <div
                key={index}
                ref={(el) => {
                  stepRefs.current[index] = el;
                  if (el) el.dataset.step = index;
                }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? 'lg:grid-flow-row' : 'lg:grid-flow-row-dense'
                }`}
              >
                {/* Step Content */}
                <div className={`space-y-6 ${isEven ? '' : 'lg:order-2'} ${
                  isVisible ? 'animate-in slide-in-from-left duration-700' : 'opacity-0'
                }`}>
                  {/* Step Number & Icon */}
                  <div className="flex items-center space-x-4">
                    <div className={`${step.color} w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
                      {step.step}
                    </div>
                    <div className={`${step.lightColor} ${step.borderColor} border-2 p-3 rounded-full`}>
                      <StepIcon className={`h-8 w-8 ${step.color.replace('bg-', 'text-')}`} />
                    </div>
                  </div>

                  {/* Step Title */}
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                    {step.title}
                  </h3>

                  {/* Step Description */}
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Step Details */}
                  <div className={`${step.lightColor} ${step.borderColor} border-l-4 p-4 rounded-r-lg`}>
                    <p className={`font-medium ${step.color.replace('bg-', 'text-')}`}>
                      {step.detail}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  {index === 0 && (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        onClick={handleCall}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 transform hover:scale-105 transition-all duration-200"
                      >
                        <Phone className="h-4 w-4 mr-2" />
                        Appeler maintenant
                      </Button>
                      <Button
                        onClick={scrollToContact}
                        variant="outline"
                        className="border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white font-semibold px-6 py-3 transition-all duration-200"
                      >
                        Formulaire en ligne
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Visual Element */}
                <div className={`${isEven ? '' : 'lg:order-1'} flex justify-center ${
                  isVisible ? 'animate-in slide-in-from-right duration-700 delay-300' : 'opacity-0'
                }`}>
                  <Card className="p-8 hover:shadow-xl transition-all duration-300 transform hover:scale-105 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100">
                    <CardContent className="text-center space-y-6">
                      {/* Large Icon */}
                      <div className={`${step.color} w-24 h-24 rounded-full flex items-center justify-center mx-auto shadow-2xl`}>
                        <StepIcon className="h-12 w-12 text-white" />
                      </div>
                      
                      {/* Step Number */}
                      <div className="space-y-2">
                        <div className={`${step.lightColor} ${step.borderColor} border-2 px-4 py-2 rounded-full inline-block`}>
                          <span className={`font-bold ${step.color.replace('bg-', 'text-')}`}>
                            Étape {step.step}
                          </span>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">
                          {step.title}
                        </h4>
                      </div>

                      {/* Progress Indicator */}
                      <div className="flex justify-center space-x-2">
                        {[1, 2, 3, 4].map((num) => (
                          <div
                            key={num}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                              num <= step.step 
                                ? step.color 
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Connector Arrow (except for last step) */}
                {index < steps.length - 1 && (
                  <div className="lg:col-span-2 flex justify-center my-8">
                    <div className={`${isVisible ? 'animate-in fade-in duration-1000 delay-500' : 'opacity-0'}`}>
                      <ArrowRight className="h-8 w-8 text-yellow-400 transform rotate-90 lg:rotate-0" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-2xl p-8 lg:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-black mb-4">
              Prêt à résoudre votre problème ?
            </h3>
            <p className="text-lg text-black mb-6 opacity-90 max-w-2xl mx-auto">
              Notre équipe d'experts est disponible 7j/7 pour une intervention rapide et efficace
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleCall}
                className="bg-black hover:bg-gray-800 text-white font-bold px-8 py-4 text-lg transform hover:scale-105 transition-all duration-200"
              >
                <Phone className="h-5 w-5 mr-2" />
                06 76 58 30 38
              </Button>
              <Button
                onClick={scrollToContact}
                variant="outline"
                className="border-2 border-black text-black hover:bg-black hover:text-white font-bold px-8 py-4 text-lg transition-all duration-200"
              >
                Demander un devis
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;